import React, { useState } from 'react';
import * as THREE from 'three';
import { NodeExpander } from './NodeExpander';
import { NodeIcon } from './NodeIcon';
import { NodeLabel } from './NodeLabel';
import {
  NodeState,
  getObjectDisplayName,
  getObjectType,
  isSelectableObject,
  shouldShowInHierarchy,
} from './types';

interface TreeNodeProps {
  object: THREE.Object3D;
  level: number;
  isSelected: boolean;
  nodeStates: NodeState;
  onSelect: (object: THREE.Object3D) => void;
  onToggleExpand: (objectId: number) => void;
}

export const TreeNode: React.FC<TreeNodeProps> = ({
  object,
  level,
  isSelected,
  nodeStates,
  onSelect,
  onToggleExpand,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const hasChildren = object.children.length > 0;
  const isExpanded = nodeStates[object.id] || false;
  const visibleChildren = object.children.filter(shouldShowInHierarchy);

  const handleClick = () => {
    if (isSelectableObject(object)) {
      onSelect(object);
    }
  };

  const handleToggleExpand = () => {
    onToggleExpand(object.id);
  };

  const backgroundColor = isSelected
    ? 'rgba(0,0,0,0.04)'
    : isHovered
    ? 'rgba(0,0,0,0.02)'
    : 'transparent';

  return (
    <>
      <div
        className='option'
        style={{
          padding: '4px',
          paddingLeft: `${4 + level * 18}px`,
          color: '#666',
          whiteSpace: 'nowrap',
          cursor: 'pointer',
          fontSize: '12px',
          display: 'flex',
          alignItems: 'center',
          backgroundColor,
        }}
        onClick={handleClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <NodeExpander
          hasChildren={hasChildren}
          isExpanded={isExpanded}
          onToggle={handleToggleExpand}
        />
        <NodeIcon objectType={getObjectType(object)} />
        <NodeLabel>{getObjectDisplayName(object)}</NodeLabel>
      </div>

      {hasChildren && isExpanded && (
        <>
          {visibleChildren.map((child) => (
            <TreeNode
              key={child.id}
              object={child}
              level={level + 1}
              isSelected={isSelected}
              nodeStates={nodeStates}
              onSelect={onSelect}
              onToggleExpand={onToggleExpand}
            />
          ))}
        </>
      )}
    </>
  );
};
